import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { changeSessionScope } from "./lib/session-scope";
import { setReportOwner } from "./state/reports";
import { resetCases } from "./hooks/useCases";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { LangContext, useLangState, useT } from "./i18n";
import { Frame } from "./shell/Frame";
import { Today } from "./pages/Today";
import { MyJobs } from "./pages/MyJobs";
import { WorkOrder } from "./pages/WorkOrder";
import { Completion } from "./pages/Completion";
import { Statement } from "./pages/Statement";
import { SignIn } from "./pages/SignIn";
import { SiteRecord } from "./pages/SiteRecord";
import { LayDiagram } from "./pages/LayDiagram";
import {Team} from './pages/Team';
import {Pricing} from './pages/Pricing';
import { currentSession, supabase } from "./lib/auth";
import { env } from "./lib/env";

export function App() {
  const lang = useLangState();
  return (
    <LangContext.Provider value={lang}>
      <Gate />
    </LangContext.Provider>
  );
}

function Gate() {
  const { t } = useT();
  const [identity, setIdentity] = useState<{owner:string|null;version:number}|null>(null);
  useEffect(() => {
    let alive=true, eventSeen=false, owner:string|null|undefined, token:string|null|undefined;
    const accept=(nextOwner:string|null,nextToken:string|null)=>{
      if(!alive || (owner===nextOwner && token===nextToken))return;
      owner=nextOwner;token=nextToken;
      const next=changeSessionScope(owner,token);
      resetCases();setReportOwner(owner);
      setIdentity({owner,version:next.version});
    };
    if(env.mock){accept('mock',null);return()=>{alive=false;changeSessionScope(null,null);resetCases();setReportOwner(null);};}
    const sub=supabase()?.auth.onAuthStateChange((event,s)=>{
      if(event==='INITIAL_SESSION'&&eventSeen)return;
      eventSeen=true;accept(s?.user.id??null,s?.access_token??null);
    });
    // An older initial lookup must never restore an account after a newer event.
    void currentSession().then(s=>{if(!eventSeen)accept(s?.user.id??null,s?.access_token??null);},()=>{if(!eventSeen)accept(null,null);});
    return()=>{alive=false;sub?.data.subscription.unsubscribe();changeSessionScope(null,null);resetCases();setReportOwner(null);};
  }, []);
  if(identity===null)return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">{t("signing_in")}</div>;
  if(!identity.owner)return <SignIn />;
  return <AccountApp key={identity.version} />;
}

function AccountApp(){
  const [cache]=useState(()=>new QueryClient({defaultOptions:{queries:{staleTime:15_000,retry:1,refetchOnWindowFocus:false}}}));
  useEffect(()=>()=>{void cache.cancelQueries();cache.clear();},[cache]);
  return (
    <QueryClientProvider client={cache}><BrowserRouter>
      <Routes>
        <Route element={<Frame />}>
          <Route index element={<Today />} />
          <Route path="jobs" element={<MyJobs />} />
          <Route path="work-order" element={<WorkOrder />} />
          <Route path="work-order/:ref" element={<WorkOrder />} />
          <Route path="work-order/:ref/set-out" element={<LegacySetOutRedirect />} />
          <Route path="work-order/:ref/completion" element={<Completion />} />
          <Route path="work-order/:ref/site-record" element={<SiteRecord />} />
          <Route path="work-order/:ref/lay-diagram" element={<LayDiagram />} />
          <Route path="owed" element={<Statement />} />
          <Route path="team" element={<Team />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="*" element={<NotHere />} />
        </Route>
      </Routes>
    </BrowserRouter></QueryClientProvider>
  );
}

function LegacySetOutRedirect(){const {ref}=useParams();return <Navigate to={`/work-order/${ref}/completion`} replace/>;}

function NotHere() {
  const { t } = useT();
  return <p className="text-sm text-muted-foreground">{t("not_here")} · {t("no_such_page")}</p>;
}
